import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { AppVersion } from 'app/version';
import { ConfigKey } from './config-keys';
import { ConfigDbService } from './config-db.service';
import { LoggingService } from './logging.service';
import {
  isCapacitorNative,
  isE2ETest,
  isPlatformAndroid,
  isPlatformIOS,
} from '../environments/environment-helpers';
import {
  chooseBackend,
  claimReportOnce,
  decideMinimumVersionStatus,
  DISMISS_KEY_PREFIX,
  E2E_FAKE_VERSION_CHECK_KEY,
  E2EFakeVersionCheck,
  isDismissedRecently,
  makeReportOnceState,
  makeRequiredPromptState,
  makeThrottleState,
  MinimumVersionStatus,
  parseE2EFakeVersionCheck,
  RequiredPromptAction,
  runRequiredPromptCheckAsync,
  recordDismissal,
  rememberQuery,
  shouldServeFromCache,
  supportsUpdateCheckingFor,
  UpdateInstallState,
  VersionCheckBackend,
  VersionCheckPlatformFlags,
  VersionCheckResult,
  VersionCheckStorage,
  VersionCheckSurface,
  VersionCheckThrottleState,
} from './version-check.dispatch';
import { VersionCheckAndroidBackend } from './version-check.android';
import { VersionCheckIosBackend } from './version-check.ios';
import { VersionCheckPwaBackend } from './version-check.pwa';

export {
  MinimumVersionStatus,
  RequiredPromptAction,
  UpdateInstallState,
  VersionCheckBackend,
  VersionCheckResult,
  VersionCheckSurface,
} from './version-check.dispatch';

// The Angular-side wrapper around the pure dispatch helpers in
// version-check.dispatch.ts. Holds the per-session throttle state and
// the most recent backend result, and exposes the same five methods
// the PRD's §1 interface specifies.
//
// See design-docs/in-app-update-prompt.md for the architecture
// overview and the supportsUpdateChecking() decision table.
@Injectable({ providedIn: 'root' })
export class VersionCheckService {

  // Per-session "I have already queried this surface" markers + the
  // most recent backend answer. The throttle is in-memory only —
  // cold start / page reload clears it, which is exactly what we
  // want: after a reload the user is on a new bundle, so the prior
  // cached "update available" answer is stale anyway. Extracted to
  // version-check.dispatch.ts so the state machine is unit-testable
  // without pulling Angular through ts-node.
  private throttle: VersionCheckThrottleState = makeThrottleState();

  // #6770: a malformed config/MinimumSupportedVersion is reported once per process,
  // and Home's "Please update" alert opens at most once per process.
  // Both are in memory on purpose: a cold start or a web reload is a new process and
  // gets a fresh answer.
  private malformedMinimumReport = makeReportOnceState();
  private requiredPrompt = makeRequiredPromptState();

  // The last answer of getMinimumVersionStatusAsync, readable synchronously by the
  // telemetry callers that hold no status of their own (the Settings row, the
  // Android `completed` event). NotSet until the first read.
  lastMinimumStatus: MinimumVersionStatus = MinimumVersionStatus.NotSet;

  constructor(
    private androidBackend: VersionCheckAndroidBackend,
    private pwaBackend: VersionCheckPwaBackend,
    private iosBackend: VersionCheckIosBackend,
    private configDb: ConfigDbService,
    private logging: LoggingService,
  ) {}

  supportsUpdateChecking(): boolean {
    // E2E-only fake (#6770): with its localStorage key set, an E2E run (or the
    // owner's flagged dev tab) sees an available update, whatever the hostname.
    // Checked before the E2E stub below, which it overrides.
    if (this.e2eFake()) return true;
    // E2E stub: the harness needs a deterministic "no update
    // available" baseline so existing dashboard coverage sees the
    // layout unchanged when this surface lands (PRD acceptance
    // §E2E). Returning false here is equivalent to the harness
    // mocking both supportsUpdateChecking → false and
    // getStatusAsync → {updateAvailable:false}, since the dashboard's
    // CsUpdateAvailableNotice.refreshAsync early-returns on the first
    // one and never reaches the second. Tests that specifically cover
    // the new card can override via a future per-test fixture.
    if (isE2ETest()) return false;
    return supportsUpdateCheckingFor(this.platformFlags());
  }

  // surface defaults to 'settings' because Settings is unthrottled —
  // any direct caller without an explicit surface gets the safe
  // "always query" behavior. Dashboard callers must pass 'startup' or
  // 'resume' to benefit from the throttle.
  async getStatusAsync(surface: VersionCheckSurface = 'settings'): Promise<VersionCheckResult> {
    if (!this.supportsUpdateChecking()) return { updateAvailable: false };

    const fake = this.e2eFake();
    if (fake) return { updateAvailable: true, availableVersion: fake.availableVersion };

    if (shouldServeFromCache(this.throttle, surface)) {
      return this.throttle.cachedResult!;
    }

    const result = await this.resolveBackend().getStatusAsync();
    rememberQuery(this.throttle, surface, result);
    return result;
  }

  async startUpdateAsync(): Promise<void> {
    if (!this.supportsUpdateChecking()) return;
    if (this.e2eFake()) {
      console.log('VersionCheckService: E2E fake, startUpdateAsync does nothing');
      return;
    }
    await this.resolveBackend().startUpdateAsync(this.isBelowMinimum());
  }

  // The "Please update" alert's Update button (#6770). Android uses Play's own
  // full-screen download when Play allows it, after routing a download already
  // running or finished as startUpdateAsync does; iOS and web do what
  // startUpdateAsync does.
  async startRequiredUpdateAsync(): Promise<void> {
    if (!this.supportsUpdateChecking()) return;
    if (this.e2eFake()) {
      console.log('VersionCheckService: E2E fake, startRequiredUpdateAsync does nothing');
      return;
    }
    await this.resolveBackend().startRequiredUpdateAsync(this.isBelowMinimum());
  }

  // Reads config/MinimumSupportedVersion (or the E2E fake's minimum) and compares
  // it with the running build. A malformed value nags nobody and is reported once
  // per process. See design-docs/in-app-update-prompt.md, "Minimum supported version".
  async getMinimumVersionStatusAsync(): Promise<MinimumVersionStatus> {
    const fake = this.e2eFake();
    const value = fake
      ? fake.minimumVersion
      : await this.configDb.getConfigValueAsync<unknown>(ConfigKey.MinimumSupportedVersion);
    const status = decideMinimumVersionStatus(AppVersion, value);
    if (status === MinimumVersionStatus.Malformed && claimReportOnce(this.malformedMinimumReport)) {
      this.logging.trackSilentProblem('MinimumSupportedVersionMalformed', { value });
    }
    this.lastMinimumStatus = status;
    return status;
  }

  // Home's check for the "Please update" alert, run on each Home entry. Opens it at
  // most once per process, only below the minimum with an update the platform
  // offers, and only while Home is still the active page. The order of the steps
  // is in runRequiredPromptCheckAsync.
  checkRequiredPromptAsync(home: {
    isHomeActive(): boolean;
    waitNavigationSettledAsync(): Promise<void>;
    openAlert(availableVersion: string): void;
  }): Promise<RequiredPromptAction> {
    return runRequiredPromptCheckAsync(this.requiredPrompt, {
      getMinimumVersionStatusAsync: () => this.getMinimumVersionStatusAsync(),
      getStatusAsync: () => this.getStatusAsync('startup'),
      installState: () => this.installState(),
      waitNavigationSettledAsync: () => home.waitNavigationSettledAsync(),
      isHomeActive: () => home.isHomeActive(),
      openAlert: availableVersion => home.openAlert(availableVersion),
    });
  }

  // True when the last minimum read said BelowMinimum, whether or not an update
  // exists. Sent as `below_minimum` on every app_update event.
  isBelowMinimum(): boolean {
    return this.lastMinimumStatus === MinimumVersionStatus.BelowMinimum;
  }

  // Finishes a downloaded update by restarting the app — the Android
  // flexible-update "Restart" action. No-op on PWA/iOS (their backends
  // never reach the Ready state). Called when the user taps Restart on
  // the dashboard card.
  async completeUpdateAsync(): Promise<void> {
    if (!this.supportsUpdateChecking()) return;
    if (this.e2eFake()) {
      console.log('VersionCheckService: E2E fake, completeUpdateAsync does nothing');
      return;
    }
    await this.resolveBackend().completeUpdateAsync(this.isBelowMinimum());
  }

  // Live install-progress state for the home-dashboard card to bind to.
  // Reading this in a template auto-tracks under zoneless change
  // detection, so the card re-renders when the active backend moves
  // through Downloading → Ready. Returns Idle on unsupported platforms
  // (no backend to ask). Only the Android backend ever leaves Idle.
  installState(): UpdateInstallState {
    if (!this.supportsUpdateChecking()) return UpdateInstallState.Idle;
    if (this.e2eFake()) return UpdateInstallState.Idle;
    return this.resolveBackend().installState();
  }

  // The two dismissal methods below are `async` even though their
  // bodies have no `await` — the `Async` suffix and `Promise` return
  // shape are part of the PRD §1 public contract so that a future
  // platform-specific storage path (Capacitor Preferences on iOS,
  // SharedPreferences on Android, ...) can swap in without breaking
  // every caller. Do not "simplify" them to synchronous methods.
  async isDismissedRecentlyAsync(availableVersion: string): Promise<boolean> {
    return isDismissedRecently(this.storage(), availableVersion, DateTime.now());
  }

  async recordDismissalAsync(availableVersion: string): Promise<void> {
    recordDismissal(this.storage(), availableVersion, DateTime.now());
  }

  // The E2E fake's fixture, or null. Read only while an E2E run is flagged; outside
  // one the key is never looked at. A broken fixture is reported and ignored, so the
  // test that wrote it fails on the missing page rather than on a thrown render.
  private e2eFake(): E2EFakeVersionCheck | null {
    if (!isE2ETest()) return null;
    try {
      return parseE2EFakeVersionCheck(localStorage.getItem(E2E_FAKE_VERSION_CHECK_KEY));
    } catch (err) {
      this.logging.trackSilentProblem('VersionCheckE2EFakeBroken', { message: (err as Error)?.message });
      return null;
    }
  }

  private resolveBackend(): VersionCheckBackend {
    return chooseBackend(this.platformFlags(), {
      android: this.androidBackend,
      pwa: this.pwaBackend,
      ios: this.iosBackend,
    });
  }

  private platformFlags(): VersionCheckPlatformFlags {
    return {
      isCapacitorNative: isCapacitorNative(),
      isPlatformAndroid: isPlatformAndroid(),
      isPlatformIOS: isPlatformIOS(),
      // Read at call-time (not constructor-time) so the answer reflects
      // the live runtime — e.g. an SW registered after the service
      // already exists.
      hasServiceWorker: typeof navigator !== 'undefined' && !!navigator.serviceWorker,
      hostname: typeof location !== 'undefined' ? location.hostname : '',
    };
  }

  private storage(): VersionCheckStorage {
    return localStorage;
  }
}

// Re-exported so consumers can introspect the dismissal-key prefix
// (e.g. for cleanup tooling) without reaching into the pure-helpers
// module directly.
export { DISMISS_KEY_PREFIX };
